import { FeatureTypeConfig } from "../../components/dataCatalog/config/featureTypes";

/**
 * Builds the plain-text place description(s) for `parametersToQueryText.js`/`parametersToSegments.js`
 * to join into their own "in {places}" connector. Multiple places drilled into the same geography
 * level (the backend's `PlacesInPlace`) each carry a WHOLE composed phrase as their own `name`
 * field directly -- e.g. "state lower houses in Missouri" -- with no separate "parent name" field
 * to read back out, so this works structurally instead: when places share a `level`, find the
 * longest string every one of their `generateQueryString` outputs has in common. A REAL shared
 * "{level plural} in " prefix always ends in whitespace and leaves a genuine place name after it
 * on every one -- two places that merely start with the same letters (e.g. "Washington" and
 * "Washington, D.C.") don't satisfy both conditions, so they're left unmerged.
 */
export function combinePlaceParts(places) {
  if (!places || places.length === 0) return [];
  if (places.length === 1) {
    return [FeatureTypeConfig.places.generateQueryString(places[0])];
  }

  const level = places[0].level;
  const sharesLevel = Boolean(level) && places.every((place) => place.level === level);
  if (sharesLevel) {
    const names = places.map((place) => FeatureTypeConfig.places.generateQueryString(place));
    const prefix = longestCommonPrefix(names);
    const suffixes = names.map((name) => name.slice(prefix.length));
    const isGenuineSharedPrefix = /\s$/.test(prefix) && suffixes.every((suffix) => suffix.length > 0);
    if (isGenuineSharedPrefix) {
      return [`${prefix}${suffixes.join(" and ")}`];
    }
  }

  return places.map((place) => FeatureTypeConfig.places.generateQueryString(place));
}

function longestCommonPrefix(strings) {
  if (strings.length === 0) return "";
  let prefix = strings[0];
  for (const value of strings.slice(1)) {
    while (prefix && !value.startsWith(prefix)) {
      prefix = prefix.slice(0, -1);
    }
    if (!prefix) return "";
  }
  return prefix;
}
